import Component from '@glimmer/component'
import {
  AlertType,
  InlineAlert,
  InlineAlertTitle,
  InlineAlertSubtitle,
} from 'teacher-ui-client/components/inline-alert'
import { t } from 'ember-intl'
import type { BaseReportMetaStandardsSet } from 'teacher-ui-client/services/reporting'
import type SessionService from 'teacher-ui-client/services/session'
import { service } from '@ember/service'
import { isAfter, sub } from 'date-fns'
import type StandardsSet from 'teacher-ui-client/models/standards-set'

interface Signature {
  Args: {
    report: Nullable<{
      meta: BaseReportMetaStandardsSet
    }>
  }
}

export class StandardsSetUpdatedAlert extends Component<Signature> {
  @service session!: SessionService

  get school() {
    return this.session.currentSchool
  }

  /* Only show when the current standards does not match the standards that the report was generated with.
   * Explicitly exclude undefined from showing the alert, this may be the case during loading of a report.
   */
  get showAlert() {
    return this.args.report && standardsSetRecentlyUpdated(this.school, this.args.report)
  }

  <template>
    {{#if this.showAlert}}
      <InlineAlert data-test-report-standards-updated-alert @type={{AlertType.Warning}}>
        <InlineAlertTitle> {{t "reporting.alerts.standardsAlert.title"}}</InlineAlertTitle>
        <InlineAlertSubtitle>
          <span>{{t
              "reporting.alerts.standardsAlert.currentStandards"
              htmlSafe=true
              name=this.school.standardsSet.name
            }}</span>
          <span>{{t "reporting.alerts.standardsAlert.leadTime" htmlSafe=true}}</span><br />
          <span>{{t "reporting.alerts.standardsAlert.oldDataWarning"}}</span>
        </InlineAlertSubtitle>
      </InlineAlert>
    {{/if}}
  </template>
}

function standardsSetRecentlyUpdated(
  { standardsSet, updatedAt }: { standardsSet?: StandardsSet; updatedAt?: Date },
  report: {
    meta: BaseReportMetaStandardsSet
  },
) {
  return standardsSetUpdated({ standardsSet }, report) && isWithinPast24Hours(updatedAt)
}

function standardsSetUpdated(
  { standardsSet }: { standardsSet?: StandardsSet },
  report: {
    meta: BaseReportMetaStandardsSet
  },
) {
  if (!standardsSet) return false

  return !reportHasMatchingStandardsSet(report, standardsSet)
}

function reportHasMatchingStandardsSet(report: { meta: BaseReportMetaStandardsSet }, standardsSet: StandardsSet) {
  return Boolean(report.meta.standards_set_id) && report.meta.standards_set_id === standardsSet.id
}

function isWithinPast24Hours(date?: Date) {
  if (!date) {
    return true // If no date is provided from BE default to existing behaviour
  }
  const now = new Date()
  const twentyFourHoursAgo = sub(now, { hours: 24 })
  return isAfter(date, twentyFourHoursAgo)
}
